import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Select,
  Box,
  MenuItem,
  Typography,
  Alert,
} from '@mui/material';
import {
  buildDefaultYearMonthSelections,
  buildYearMonthRangeGroups,
  MONTH_TRANSLATION_KEYS,
  validateYearMonthSelections,
} from '../../Utils/ReportUtils.js';
import './ProfitAndLossDownloadModal.css';

const ProfitAndLossDownloadModal = ({
  open,
  onClose,
  onConfirm,
  isMonthly,
  startDate,
  endDate,
  direction,
  t,
}) => {
  const [withPct, setWithPct] = useState(true);
  const [detailedEnabled, setDetailedEnabled] = useState(false);
  const [yearSelections, setYearSelections] = useState({});
  const [validationMessage, setValidationMessage] = useState('');

  const yearGroups = useMemo(() => {
    if (!isMonthly || !startDate || !endDate) return [];
    return buildYearMonthRangeGroups(startDate, endDate);
  }, [isMonthly, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    setWithPct(true);
    setDetailedEnabled(false);
    setValidationMessage('');
    setYearSelections(buildDefaultYearMonthSelections(yearGroups));
  }, [open, yearGroups]);

  const handleYearSelectionChange = (year, field, value) => {
    setYearSelections((prev) => {
      const current = prev[year] ?? {};
      const next = {
        ...current,
        [field]: Number(value),
      };

      if (next.fromMonth && next.toMonth && next.fromMonth > next.toMonth) {
        if (field === 'fromMonth') {
          next.toMonth = next.fromMonth;
        } else {
          next.fromMonth = next.toMonth;
        }
      }

      return {
        ...prev,
        [year]: next,
      };
    });
    setValidationMessage('');
  };

  const handleConfirm = () => {
    if (detailedEnabled) {
      const validation = validateYearMonthSelections(yearGroups, yearSelections, t);
      if (!validation.isValid) {
        setValidationMessage(validation.message);
        return;
      }

      onConfirm({
        withPct,
        showDetailedMonthlyBreakdown: true,
        selectedMonths: validation.selectedMonths,
      });
      return;
    }

    onConfirm({
      withPct,
      showDetailedMonthlyBreakdown: false,
      selectedMonths: null,
    });
  };

  const getAvailableToMonths = (group) => {
    const fromMonth = yearSelections[group.year]?.fromMonth ?? group.minMonth;
    return group.availableMonths.filter((month) => month >= fromMonth);
  };

  const getAvailableFromMonths = (group) => {
    const toMonth = yearSelections[group.year]?.toMonth ?? group.maxMonth;
    return group.availableMonths.filter((month) => month <= toMonth);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      dir={direction}
      className={`pl-download-modal ${direction}`}
      PaperProps={{ sx: { width: '100%', maxWidth: 560, m: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>{t('DownloadProfitAndLossModalTitle')}</DialogTitle>
      <DialogContent className="pl-download-modal-content">
        <Box className="pl-download-section">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            <Trans
              i18nKey="IncludePercentagesModalText"
              components={{ bold: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }} /> }}
            />
          </Typography>
          <RadioGroup
            row
            value={withPct ? 'yes' : 'no'}
            onChange={(event) => setWithPct(event.target.value === 'yes')}
            className="pl-download-radio-group"
          >
            <FormControlLabel value="yes" control={<Radio />} label={t('Yes')} />
            <FormControlLabel value="no" control={<Radio />} label={t('No')} />
          </RadioGroup>
        </Box>

        {isMonthly && (
          <>
            <Box className="pl-download-section pl-download-section-divider">
              <FormControlLabel
                className="pl-download-checkbox"
                control={
                  <Checkbox
                    checked={detailedEnabled}
                    onChange={(event) => {
                      setDetailedEnabled(event.target.checked);
                      setValidationMessage('');
                    }}
                  />
                }
                label={t('DownloadDetailedMonthlyReportCheckbox')}
              />
            </Box>

            {detailedEnabled && (
              <Box className="pl-download-years-panel">
                {yearGroups.map((group) => (
                  <Box key={group.year} className="pl-download-year-group">
                    <Typography component="div" className="pl-download-year-header">
                      {group.year}
                    </Typography>
                    <Box className="pl-download-month-row">
                      <Box className="pl-download-month-field">
                        <Typography component="label" className="pl-download-month-label">
                          {t('FromMonth')}
                        </Typography>
                        <Select
                          fullWidth
                          size="small"
                          variant="outlined"
                          className="pl-download-month-select"
                          value={yearSelections[group.year]?.fromMonth ?? group.minMonth}
                          onChange={(event) =>
                            handleYearSelectionChange(group.year, 'fromMonth', event.target.value)
                          }
                          MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                        >
                          {getAvailableFromMonths(group).map((month) => (
                            <MenuItem dir={direction} key={`from-${group.year}-${month}`} value={month}>
                              {t(MONTH_TRANSLATION_KEYS[month - 1])}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      <Box className="pl-download-month-field">
                        <Typography component="label" className="pl-download-month-label">
                          {t('ToMonth')}
                        </Typography>
                        <Select
                          fullWidth
                          size="small"
                          variant="outlined"
                          className="pl-download-month-select"
                          value={yearSelections[group.year]?.toMonth ?? group.maxMonth}
                          onChange={(event) =>
                            handleYearSelectionChange(group.year, 'toMonth', event.target.value)
                          }
                          MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                        >
                          {getAvailableToMonths(group).map((month) => (
                            <MenuItem dir={direction} key={`to-${group.year}-${month}`} value={month}>
                              {t(MONTH_TRANSLATION_KEYS[month - 1])}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {validationMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="contained" onClick={handleConfirm} sx={{backgroundColor: '#304FFF', '&:hover': { backgroundColor: '#2538d9' } }}>
            {t('Download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfitAndLossDownloadModal;
